<template>
  <div class="page_container content-box">
    <!-- Campaign Info Section -->
    <div v-if="campaign" class="campaign-info-section">
      <woot-button
        class="pl_0 mb_8"
        variant="clear"
        icon="chevron-left"
        color-scheme="secondary"
        @click="goBack"
      >
        {{ $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.BACK') }}
      </woot-button>
      <h3 class="info-section-title">
        {{ $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.INFO_TITLE') }}
      </h3>

      <div class="info-cards-grid">
        <!-- Date Range Card -->
        <div class="info-card">
          <div class="info-card-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </div>
          <div class="info-card-content">
            <div class="info-card-label">
              {{ $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.DATE_RANGE') }}
            </div>
            <div class="info-card-value">
              {{ formatDateRange(campaign.start_date, campaign.end_date) }}
            </div>
          </div>
        </div>

        <!-- Time Range Card -->
        <div class="info-card">
          <div class="info-card-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div class="info-card-content">
            <div class="info-card-label">
              {{ $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.TIME_RANGE') }}
            </div>
            <div class="info-card-value">
              {{ formatTimeRange(campaign.time_start, campaign.time_end) }}
            </div>
          </div>
        </div>

        <!-- Worker Count Card -->
        <div class="info-card">
          <div class="info-card-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div class="info-card-content">
            <div class="info-card-label">
              {{ $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.WORKER_COUNT') }}
            </div>
            <div class="info-card-value">
              {{ campaign.worker_count }}
              <Label
                v-if="campaign.call_settings.workers_active !== undefined"
                :title="
                  campaign.call_settings.workers_active
                    ? $t(
                        'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.WORKER_STATUS_ACTIVE'
                      )
                    : $t(
                        'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.WORKER_STATUS_INACTIVE'
                      )
                "
                :color-scheme="
                  campaign.call_settings.workers_active
                    ? 'success'
                    : 'secondary'
                "
                size="small"
              />
            </div>
          </div>
        </div>

        <!-- Exclude Days Card -->
        <div class="info-card">
          <div class="info-card-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <line x1="9" y1="9" x2="15" y2="15" />
              <line x1="15" y1="9" x2="9" y2="15" />
            </svg>
          </div>
          <div class="info-card-content">
            <div class="info-card-label">
              {{ $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.EXCLUDE_DAYS') }}
            </div>
            <div class="info-card-value">
              <span
                v-if="
                  campaign.call_settings.exclude_days &&
                    campaign.call_settings.exclude_days.length
                "
                class="exclude-days-tags"
              >
                <span
                  v-for="day in campaign.call_settings.exclude_days"
                  :key="day"
                  class="day-tag"
                >
                  {{ formatDayName(day) }}
                </span>
              </span>
              <span v-else class="no-exclude-days">
                {{
                  $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.NO_EXCLUDE_DAYS')
                }}
              </span>
            </div>
          </div>
        </div>

        <!-- LLM Chatbot Card -->
        <div class="info-card">
          <div class="info-card-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="3" y="11" width="18" height="10" rx="2" />
              <circle cx="12" cy="5" r="2" />
              <path d="M12 7v4" />
              <line x1="8" y1="16" x2="8" y2="16" />
              <line x1="16" y1="16" x2="16" y2="16" />
            </svg>
          </div>
          <div class="info-card-content">
            <div class="info-card-label">
              {{ $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.AGENT_NAME') }}
            </div>
            <div class="info-card-value">
              {{ campaign.agent_name || '-' }}
            </div>
          </div>
        </div>

        <!-- Updated At Card -->
        <div class="info-card">
          <div class="info-card-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
              />
              <path
                d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
              />
            </svg>
          </div>
          <div class="info-card-content">
            <div class="info-card-label">
              {{ $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.UPDATED_AT') }}
            </div>
            <div class="info-card-value">
              {{ formatDateTime(campaign.updated_at) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Header with Back Button, Title and Status -->
    <div class="campaign-header">
      <div v-if="campaign" class="header-left">
        <h2 class="campaign-title">
          {{ campaign.name }}
        </h2>
        <Label
          :title="formatStatus(campaign.status)"
          :color-scheme="getStatusColor(campaign.status)"
        />
      </div>
      <!-- Date Range Picker -->
      <div class="audience-date-range header-right">
        <DateRangePicker
          class="mb-0"
          :value="dateRange"
          :placeholder="$t('CAMPAIGN.FILTERS.DATE_PLACEHOLDER')"
          :confirm-text="$t('CAMPAIGN.FILTERS.DATE_CONFIRM_BTN')"
          display-format="D MMM YYYY"
          @change="handleDateChange"
        />
        <div class="filters__button-wrap">
          <div
            v-if="appliedFilters.length"
            class="filters__applied-indicator"
          />
          <woot-button
            class="margin-right-small clear"
            color-scheme="secondary"
            icon="filter"
            @click="onToggleFilter"
          >
            {{ $t('CONTACTS_PAGE.FILTER_CONTACTS') }}
          </woot-button>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div v-if="metaReports" class="stats-section">
      <div class="report_static_metric_cards">
        <woot-report-stats-card
          v-for="metric in audienceMetrics"
          :key="metric.KEY"
          :desc="metric.DESC"
          :heading="metric.NAME"
          :point="metaReports[metric.KEY]"
        />
      </div>
    </div>

    <!-- Detail Report Toggle Button -->
    <div class="see-more-button-wrapper">
      <woot-button
        variant="clear"
        color-scheme="secondary"
        :icon="showAudienceSection ? 'chevron-up' : 'chevron-down'"
        @click="toggleDetailReportSection"
      >
        {{
          showAudienceSection
            ? $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.SEE_LESS')
            : $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.SEE_MORE')
        }}
      </woot-button>
    </div>
    <!-- Detail Report Section - Collapsible -->
    <transition name="slide-fade">
      <div v-show="showAudienceSection" class="detail-report-section">
        <h3 class="section-title">
          {{
            $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.DETAIL_REPORT_TITLE')
          }}
        </h3>
        <!-- Report Period Filter -->
        <div class="audience-filters audience-filters--end">
          <div class="audience-filter">
            <select
              v-model="reportPeriod"
              class="report-period-select"
              @change="onReportPeriodChange"
            >
              <option value="days">
                {{ $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.PERIOD_DAYS') }}
              </option>
              <option value="weeks">
                {{ $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.PERIOD_WEEKS') }}
              </option>
              <option value="months">
                {{
                  $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.PERIOD_MONTHS')
                }}
              </option>
            </select>
          </div>
        </div>

        <div class="ps-table-wrap">
          <div v-if="isLoadingDetailReport" class="loading_state">
            <spinner />
          </div>

          <template v-else>
            <DetailReportTable
              :detail-report-data="detailReportData"
              :report-period="reportPeriod"
              :is-fetching="isLoadingDetailReport"
            />
          </template>
        </div>
      </div>
    </transition>

    <!-- Contact List Section - Always Visible -->
    <div class="contact-list-section">
      <h3 class="section-title">
        {{ $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.CONTACT_LIST_TITLE') }}
      </h3>
      <!-- Filters Row -->
      <div class="audience-filters">
        <div class="audience-filter">
          <SearchInput
            v-model="searchValue"
            :placeholder="$t('CAMPAIGN.FILTERS.SEARCH_PLACEHOLDER')"
            @search="onSearch"
          />
        </div>
      </div>

      <div class="ps-table-wrap">
        <div v-if="isLoadingAudiences" class="loading_state">
          <spinner />
        </div>

        <template v-else>
          <empty-state
            v-if="!audiences.length"
            :title="$t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.NO_AUDIENCES')"
          />
          <ve-table
            v-else
            :fixed-header="true"
            :columns="audienceColumns"
            :max-height="'calc(100vh - 450px)'"
            :table-data="audiences"
            :border-around="true"
          />
        </template>
      </div>

      <div v-if="meta.count > 10" class="loadMore_btn">
        <woot-button
          variant="clear"
          size="small"
          color-scheme="primary"
          :is-loading="isLoadingAudiences"
          :is-disabled="paginationCompute.isLastPage"
          @click="onPageChange(Number(meta.current_page) + 1)"
        >
          {{ paginationCompute.btnText }}
        </woot-button>
      </div>
    </div>

    <!-- Filter Modal -->
    <woot-modal
      :show.sync="showFiltersModal"
      :on-close="onToggleFilter"
      size="medium"
    >
      <AudienceFilter
        :on-close="onToggleFilter"
        :filter-items="filterItems"
        :initial-applied-filters="appliedFilters"
        :filter-attributes="filterAttributes"
        @applyFilter="onApplyFilter"
        @clearFilters="clearFilters"
      />
    </woot-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { VeTable } from 'vue-easytable';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import Label from 'dashboard/components/ui/Label';
import SearchInput from 'dashboard/components/ui/SearchInput.vue';
import DateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import AudienceFilter from './AudienceFilter.vue';
import {
  AudienceFilterTypes,
  AudienceFilterAttributList,
} from '../broadcastAgent/aiCampaignConstant';
import helperMixin from 'shared/mixins/helperMixin';
import DetailReportTable from './DetailReportTable.vue';

export default {
  components: {
    VeTable,
    Spinner,
    EmptyState,
    Label,
    SearchInput,
    DateRangePicker,
    AudienceFilter,
    DetailReportTable,
  },
  mixins: [helperMixin],
  props: {
    campaignId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      campaign: null,
      audiences: [],
      meta: {
        count: 0,
        current_page: 1,
        reports: {
          retry: 0,
          not_connected: 0,
          disqualified: 0,
          failed: 0,
          success: 0,
          total: 0,
        },
      },
      showFiltersModal: false,
      appliedFilters: [],
      searchValue: '',
      dateRange: [],
      filterItems: AudienceFilterTypes.map(filter => ({
        ...filter,
        attributeName: this.$t(`${filter.attributeI18nKey}`),
      })),
      reportPeriod: 'days',
      showAudienceSection: false,
      detailReportData: null,
      isLoadingDetailReport: false,
      isLoadingAudiences: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'campaign_agents/getUIFlags',
    }),
    audienceMetrics() {
      const reports = this.metaReports;
      const total = reports.total || 0;
      const disqualified = reports.disqualified || 0;
      const noPickup = reports.no_pickup || 0;
      const success = reports.success || 0;
      const reject = reports.reject || 0;
      const retry = reports.retry || 0;

      // Calculate denominator for success/retry/reject percentages
      const effectiveTotal = total - disqualified - noPickup;

      return [
        {
          NAME: this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.METRIC.SUCCESS'
          ),
          KEY: 'success_percentage',
          DESC: `${success.toLocaleString()}/${effectiveTotal.toLocaleString()}`,
        },
        {
          NAME: this.$t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.METRIC.RETRY'),
          KEY: 'retry_percentage',
          DESC: `${retry.toLocaleString()}/${effectiveTotal.toLocaleString()}`,
        },
        {
          NAME: this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.METRIC.REJECT'
          ),
          KEY: 'reject_percentage',
          DESC: `${reject.toLocaleString()}/${effectiveTotal.toLocaleString()}`,
        },
        {
          NAME: this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.METRIC.NO_PICKUP'
          ),
          KEY: 'not_pickup_percentage',
          DESC: `${noPickup.toLocaleString()}/${total.toLocaleString()}`,
        },
        {
          NAME: this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.METRIC.DISQUALIFIED'
          ),
          KEY: 'disqualified_percentage',
          DESC: `${disqualified.toLocaleString()}/${total.toLocaleString()}`,
        },
        {
          NAME: this.$t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.METRIC.TOTAL'),
          KEY: 'total',
          DESC: '',
        },
      ];
    },
    metaReports() {
      const reports = this.meta.reports || {};
      const total = reports.total || 0;
      const disqualified = reports.disqualified || 0;
      const noPickup = reports.no_pickup || 0;
      const success = reports.success || 0;
      const reject = reports.reject || 0;
      const retry = reports.retry || 0;

      // Calculate denominator for success/retry percentages
      const effectiveTotal = total - disqualified - noPickup;

      return {
        ...reports,
        success_percentage:
          effectiveTotal > 0
            ? `${((success / effectiveTotal) * 100).toFixed(1)}%`
            : '0.0%',
        retry_percentage:
          effectiveTotal > 0
            ? `${((retry / effectiveTotal) * 100).toFixed(1)}%`
            : '0.0%',
        reject_percentage:
          effectiveTotal > 0
            ? `${((reject / effectiveTotal) * 100).toFixed(1)}%`
            : '0.0%',
        not_pickup_percentage:
          total > 0 ? `${((noPickup / total) * 100).toFixed(1)}%` : '0.0%',
        disqualified_percentage:
          total > 0 ? `${((disqualified / total) * 100).toFixed(1)}%` : '0.0%',
        total: total.toLocaleString(),
      };
    },
    filterAttributes() {
      return AudienceFilterAttributList.map(el => ({
        ...el,
        name: el.name,
      }));
    },
    audienceColumns() {
      return [
        {
          field: 'contact',
          key: 'contact',
          title: this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.TABLE.CONTACT_NAME'
          ),
          width: 250,
          align: 'left',
          fixed: 'left',
          renderBodyCell: ({ row }) => (
            <div class="contact-cell">
              <span class="contact-name">{row.contact.name || '-'}</span>
              <span class="contact-phone">{row.phone_number}</span>
            </div>
          ),
        },
        {
          field: 'call_status',
          key: 'call_status',
          title: this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.TABLE.CALL_STATUS'
          ),
          width: 150,
          align: 'center',
          renderBodyCell: ({ row }) => (
            <Label
              title={this.formatCallStatus(row.call_status)}
              colorScheme={this.getCallStatusColor(row.call_status)}
            />
          ),
        },
        {
          field: 'attempt',
          key: 'attempt',
          title: this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.TABLE.ATTEMPT'
          ),
          width: 120,
          align: 'center',
          renderBodyCell: ({ row }) => (
            <span class="attempt-cell">{row.attempt || 0}</span>
          ),
        },
        {
          field: 'last_called_at',
          key: 'last_called_at',
          title: this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.TABLE.LAST_CALLED_AT'
          ),
          width: 180,
          align: 'center',
          renderBodyCell: ({ row }) => (
            <span class="date-cell">
              {this.formatDateTime(row.last_called_at)}
            </span>
          ),
        },
        {
          field: 'next_retry_at',
          key: 'next_retry_at',
          title: this.$t(
            'CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.DETAILS.TABLE.NEXT_RETRY_AT'
          ),
          width: 180,
          align: 'center',
          renderBodyCell: ({ row }) => (
            <span class="date-cell">
              {this.formatDateTime(row.next_retry_at)}
            </span>
          ),
        },
      ];
    },
    paginationCompute() {
      const totalPages = Math.ceil(this.meta.count / 10);
      const currentPage = Number(this.meta.current_page);
      const isLastPage = currentPage >= totalPages;
      const currentCount = this.audiences.length || 0;

      const btnText = `${this.$t(
        'CAMPAIGN.LLM_CHATBOT.REPORT.LOAD_MORE_BTN'
      )} (${currentCount}/${this.meta.count})`;

      return {
        isLastPage,
        btnText,
      };
    },
    groupByParam() {
      const periodMap = {
        days: 'day',
        weeks: 'week',
        months: 'month',
      };
      return periodMap[this.reportPeriod] || 'day';
    },
  },
  watch: {
    campaignId(newVal) {
      if (newVal) {
        // Reset data when campaign changes
        this.campaign = null;
        this.audiences = [];
        this.appliedFilters = [];
        this.searchValue = '';
        this.dateRange = [];
        this.meta = {
          count: 0,
          current_page: 1,
          reports: {
            retry: 0,
            not_connected: 0,
            disqualified: 0,
            failed: 0,
            success: 0,
            total: 0,
          },
        };
        this.fetchDetailReport(1, false);
      }
    },
  },
  activated() {
    // Refresh data every time the page is opened or reopened
    this.fetchDetailReport(1, this.showAudienceSection);
  },
  methods: {
    toggleDetailReportSection() {
      this.showAudienceSection = !this.showAudienceSection;
      if (this.showAudienceSection && !this.detailReportData) {
        this.fetchDetailReport(1, true);
      }
    },
    onReportPeriodChange(event) {
      this.reportPeriod = event.target.value;
      this.fetchDetailReport(1, true, false);
    },
    async fetchDetailReport(
      page = 1,
      includeDetailReport = true,
      fetchAudiences = true
    ) {
      if (!this.campaignId) return;

      try {
        // Set loading states
        if (includeDetailReport) {
          this.isLoadingDetailReport = true;
        }
        if (fetchAudiences && (page === 1 || !includeDetailReport)) {
          this.isLoadingAudiences = true;
        }

        const params = {
          page,
          group_by: this.groupByParam,
        };

        // Add search query
        if (this.searchValue) {
          params.q = this.searchValue;
        }

        // Always include date range - use dateRange if set, otherwise use campaign dates
        if (this.dateRange && this.dateRange.length === 2) {
          const date = this.formatStartEndDate(
            this.dateRange[0],
            this.dateRange[1]
          );
          params.start_date = date.start_date;
          params.end_date = date.end_date;
        } else if (this.campaign?.start_date && this.campaign?.end_date) {
          const date = this.formatStartEndDate(
            new Date(this.campaign.start_date),
            new Date(this.campaign.end_date)
          );
          params.start_date = date.start_date;
          params.end_date = date.end_date;
        }

        // Apply filters if any
        let fetchAction = 'campaign_agents/fetchCampaignAudiences';

        if (this.appliedFilters?.length) {
          const finalFilter = this.appliedFilters.map(el => {
            if (
              el.filter_operator === 'is_present' ||
              el.filter_operator === 'is_not_present'
            ) {
              delete el.values;
            }
            return el;
          });

          // remove the query_operator on the last filter item
          if (finalFilter.length) {
            delete finalFilter[finalFilter.length - 1].query_operator;
          }

          params.payload = finalFilter;
          fetchAction = 'campaign_agents/fetchCampaignAudiencesFilterList';
        }

        const response = await this.$store.dispatch(fetchAction, {
          campaignId: this.campaignId,
          params,
        });

        if (response) {
          // Set campaign and meta data
          if (response.campaign) {
            this.campaign = response.campaign;
          }
          if (response.meta) {
            this.meta = response.meta;
          }

          // Set initial date range based on campaign dates if not already set
          if (
            page === 1 &&
            this.dateRange.length === 0 &&
            this.campaign?.start_date &&
            this.campaign?.end_date
          ) {
            this.dateRange = [
              new Date(this.campaign.start_date),
              new Date(this.campaign.end_date),
            ];
          }

          // Handle audiences data only if we're fetching them
          if (fetchAudiences && response.audiences !== undefined) {
            if (page === 1) {
              this.audiences = response.audiences || [];
            } else {
              this.audiences = [
                ...this.audiences,
                ...(response.audiences || []),
              ];
            }
          }

          // Transform detail report data if available
          if (includeDetailReport && response.data) {
            const periods = response.data.map((item, index) => {
              const periodDate = new Date(item.period);
              const weekNum = Math.ceil(periodDate.getDate() / 7);

              return {
                key: `period_${index}`,
                date: item.period,
                week: weekNum,
              };
            });

            const data = {};
            response.data.forEach((item, index) => {
              data[`period_${index}`] = {
                success: item.success || 0,
                reject: item.reject || 0,
                disqualified: item.disqualified || 0,
                no_pickup: item.no_pickup || 0,
                total_attempt: item.total || 0,
                total_call_minute: item.total_call_times || 0,
                total_connected_minute: item.total_connected_minute || 0,
              };
            });

            this.detailReportData = {
              periods,
              data,
            };
          }
        }
      } catch (error) {
        console.error('Error fetching campaign data:', error);
      } finally {
        this.isLoadingDetailReport = false;
        this.isLoadingAudiences = false;
      }
    },
    goBack() {
      this.$router.push({ name: 'outbound_call_campaigns' });
    },
    onSearch(value) {
      this.searchValue = value;
      this.fetchDetailReport(1, this.showAudienceSection);
    },
    handleDateChange(dateRange) {
      this.dateRange = dateRange;
      this.fetchDetailReport(1, this.showAudienceSection);
    },
    onToggleFilter() {
      this.showFiltersModal = !this.showFiltersModal;
    },
    onApplyFilter(filters) {
      this.appliedFilters = filters;
      this.fetchDetailReport(1, this.showAudienceSection);
      this.showFiltersModal = false;
    },
    clearFilters() {
      this.appliedFilters = [];
      this.fetchDetailReport(1, this.showAudienceSection);
    },
    onPageChange(page) {
      this.fetchDetailReport(page, false);
    },
    formatStatus(status) {
      if (!status) return '-';
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    getStatusColor(status) {
      switch (status) {
        case 'pending':
          return 'warning';
        case 'active':
        case 'running':
          return 'success';
        case 'completed':
          return 'secondary';
        case 'deactivate':
          return 'warning';
        default:
          return 'secondary';
      }
    },
    formatCallStatus(status) {
      if (!status) {
        return '-';
      }

      const mappedStatus = {
        not_connected: 'Not Connected',
        no_pickup: 'Not Connected',
        retry: 'Retry',
        disqualified: 'Disqualified',
        reject: 'Failed',
        failed: 'Failed',
        success: 'Success',
      };

      const finalStatus = mappedStatus[status] || status;

      return (
        finalStatus.charAt(0).toUpperCase() +
        finalStatus.slice(1).replace(/_/g, ' ')
      );
    },
    getCallStatusColor(status) {
      switch (status) {
        case 'success':
          return 'success';
        case 'failed':
        case 'reject':
          return 'alert';
        case 'retry':
          return 'warning';
        case 'not_connected':
        case 'no_pickup':
          return 'secondary';
        case 'disqualified':
          return 'warning';
        default:
          return 'secondary';
      }
    },
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
    formatTime(time) {
      if (!time) return '-';
      const date = new Date(time);
      const hours = date.getHours();
      const minutes = date.getMinutes();
      return `${hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
    },
    formatDateTime(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatDateRange(startDate, endDate) {
      if (!startDate || !endDate) return '-';
      const start = new Date(startDate);
      const end = new Date(endDate);
      return `${start.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })} - ${end.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })}`;
    },
    formatTimeRange(startTime, endTime) {
      if (!startTime || !endTime) return '-';
      const start = new Date(startTime);
      const end = new Date(endTime);
      const formatTime = date => {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        return `${hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
      };
      return `${formatTime(start)} - ${formatTime(end)}`;
    },
    formatDayName(dayCode) {
      const dayMap = {
        mon: 'Mon',
        tue: 'Tue',
        wed: 'Wed',
        thu: 'Thu',
        fri: 'Fri',
        sat: 'Sat',
        sun: 'Sun',
      };
      return dayMap[dayCode] || dayCode;
    },
    generateMockDetailReport() {
      const now = new Date();
      const periods = [];
      const data = {};

      // Generate 7 periods (last 7 days/weeks/months)
      for (let i = 6; i >= 0; i--) {
        const periodDate = new Date(now);
        periodDate.setDate(now.getDate() - i);

        const key = `period_${6 - i}`;
        const weekNum = Math.ceil(periodDate.getDate() / 7);

        periods.push({
          key,
          date: periodDate.toISOString(),
          week: weekNum,
        });

        data[key] = {
          success: Math.floor(Math.random() * 50) + 10,
          reject: Math.floor(Math.random() * 20) + 5,
          disqualified: Math.floor(Math.random() * 10) + 2,
          no_pickup: Math.floor(Math.random() * 30) + 10,
          total_attempt: Math.floor(Math.random() * 100) + 50,
          total_call_minute: Math.floor(Math.random() * 500) + 100,
          total_connected_minute: Math.floor(Math.random() * 300) + 50,
        };
      }

      return {
        periods,
        data,
      };
    },
  },
};
</script>

<style scoped lang="scss">
@import '~dashboard/assets/scss/widgets/_ps_table.scss';

.campaign-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-medium);

  .header-right {
    display: flex;
    align-items: center;
    gap: var(--space-medium);
  }

  .campaign-title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
  }
}

.stats-section {
  margin-bottom: var(--space-medium);
}

.report_static_metric_cards {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  .report-card {
    flex: 1 1 calc(16.66% - 10px);
    min-width: 140px;
  }
}

.see-more-button-wrapper {
  display: flex;
  align-items: center;
  justify-content: left;
  width: 100%;
  margin: var(--space-medium) 0;
  width: fit-content;
}

.contact-list-section {
  margin-bottom: var(--space-large);
}

.detail-report-section {
  margin-bottom: var(--space-large);
}

// Smooth transition animation
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-fade-enter-to,
.slide-fade-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.audience-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: var(--space-medium);

  .audience-filter {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &--end {
    justify-content: flex-end;
  }
}

.audience-date-range {
  margin-bottom: var(--space-medium);
}

.report-period-select {
  margin: 0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: var(--space-medium);
}

.filters__button-wrap {
  position: relative;

  .filters__applied-indicator {
    position: absolute;
    height: var(--space-small);
    width: var(--space-small);
    top: var(--space-smaller);
    right: var(--space-slab);
    background-color: var(--s-500);
    border-radius: var(--border-radius-rounded);
  }
}

.loading_state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
}

.loadMore_btn button {
  display: block;
  margin: auto;
}

.font_500 {
  font-weight: 500;
}

// Table cell styles
.contact-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;

  .contact-name {
    font-weight: 500;
    font-size: 14px;
    color: var(--s-900);
  }

  .contact-phone {
    font-size: 12px;
    color: var(--s-600);
  }
}

.phone-number-cell {
  font-weight: 500;
  color: var(--s-900);
}

.contact-name-cell {
  color: var(--s-800);
}

.attempt-cell {
  display: inline-block;
  font-weight: 600;
  font-size: 14px;
}

.date-cell {
  font-size: 13px;
  color: var(--s-700);
}

// Campaign Info Section Styles
.campaign-info-section {
  margin-bottom: var(--space-large);
}

.info-section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: var(--space-medium);
  color: var(--s-900);
}

.info-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.info-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #fff;
  border: 1px solid var(--s-200);
  border-radius: 8px;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

.info-card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--w-50);
  border-radius: 8px;
  flex-shrink: 0;
  color: var(--w-500);

  svg {
    color: var(--w-500);
  }
}

.info-card-content {
  flex: 1;
  min-width: 0;
}

.info-card-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--s-600);
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-card-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--s-900);
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.exclude-days-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.day-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: var(--s-100);
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: var(--s-700);
}

.no-exclude-days {
  font-size: 13px;
  color: var(--s-500);
  font-style: italic;
}
</style>
<style lang="scss">
.audience-date-range {
  .date-picker input {
    margin: 0;
  }
}
</style>
